import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { notification as antNotification } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  getNotifications,
  getUnreadNotificationCount,
  markNotificationRead,
  markAllNotificationsRead,
} from '../api';
import { useAuth } from './AuthContext';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || window.location.origin;

export interface NotificationItem {
  id: number;
  userId: number;
  type: string;
  title: string;
  message: string;
  entityType: string | null;
  entityId: string | null;
  linkUrl: string | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

interface NotificationContextType {
  notifications: NotificationItem[];
  unreadCount: number;
  loading: boolean;
  fetchMore: () => void;
  markRead: (id: number) => Promise<void>;
  markAllRead: () => Promise<void>;
  hasMore: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, token } = useAuth();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const socketRef = useRef<Socket | null>(null);
  const navigate = useNavigate();

  // Fetch unread count
  const refreshUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const count = await getUnreadNotificationCount();
      setUnreadCount(count);
    } catch {
      // silent
    }
  }, [token]);

  // Fetch notifications (first page)
  const fetchInitial = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const result = await getNotifications({ page: 1, limit: 20 });
      setNotifications(result.data);
      setPage(1);
      setHasMore(result.page < result.totalPages);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch more (pagination)
  const fetchMore = useCallback(async () => {
    if (!token || loading || !hasMore) return;
    setLoading(true);
    try {
      const nextPage = page + 1;
      const result = await getNotifications({ page: nextPage, limit: 20 });
      setNotifications((prev) => [...prev, ...result.data]);
      setPage(nextPage);
      setHasMore(nextPage < result.totalPages);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [token, loading, hasMore, page]);

  // Mark single as read
  const markRead = useCallback(async (id: number) => {
    try {
      await markNotificationRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // silent
    }
  }, []);

  // Mark all as read
  const markAllRead = useCallback(async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true, readAt: new Date().toISOString() })));
      setUnreadCount(0);
    } catch {
      // silent
    }
  }, []);

  // Socket.IO connection for real-time notifications
  useEffect(() => {
    if (!token || !user) return;

    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
    });
    socketRef.current = socket;

    socket.on('notification:new', (notif: NotificationItem) => {
      setNotifications((prev) => [notif, ...prev]);
      setUnreadCount((prev) => prev + 1);

      // Show toast notification
      antNotification.info({
        message: notif.title,
        description: notif.message,
        placement: 'topRight',
        duration: 5,
        onClick: () => {
          if (notif.linkUrl) {
            navigate(notif.linkUrl);
            markRead(notif.id);
          }
          antNotification.destroy();
        },
        style: { cursor: notif.linkUrl ? 'pointer' : 'default' },
      });
    });

    // On reconnect, refresh data to catch missed notifications
    socket.on('connect', () => {
      refreshUnreadCount();
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token, user, navigate, markRead, refreshUnreadCount]);

  // Initial data fetch
  useEffect(() => {
    fetchInitial();
    refreshUnreadCount();
  }, [fetchInitial, refreshUnreadCount]);

  return (
    <NotificationContext.Provider
      value={{ notifications, unreadCount, loading, fetchMore, markRead, markAllRead, hasMore }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
}
